import React, { forwardRef, useState } from 'react';

import clsx from 'clsx';
import { NumericFormat, NumericFormatProps, NumberFormatValues } from 'react-number-format';

import styles from './NumberInput.module.scss';

const DECIMAL_PLACES_AMOUNT = 0;

interface NumberInputProps extends NumericFormatProps {
  label: string;
  errorMessage?: string;
  decimalPlacesAmount?: number;
  disabled?: boolean;
  value?: string | number;
  isAllowed?: (values: NumberFormatValues) => boolean;
  inputClassName?: string;
  labelClassName?: string;
  inputContainerClassName?: string;
  useRequiredSymbol?: boolean;
}

const NumberInput = forwardRef<HTMLInputElement, NumberInputProps>(
  (
    {
      prefix,
      label,
      onChange,
      placeholder,
      className,
      inputClassName,
      labelClassName,
      inputContainerClassName,
      allowNegative,
      thousandSeparator,
      suffix,
      errorMessage,
      onFocus,
      onBlur,
      name,
      maxLength,
      max,
      disabled,
      value,
      isAllowed,
      decimalPlacesAmount = DECIMAL_PLACES_AMOUNT,
      pattern,
      useRequiredSymbol = false,
      ...rest
    },
    ref,
  ) => {
    const [isFocused, setFocus] = useState(false);
    const toggleFocusInput = () => {
      setFocus(!isFocused);
    };

    const handleOnFocus = (event: React.FocusEvent<HTMLInputElement>) => {
      toggleFocusInput();

      if (onFocus) {
        onFocus(event);
      }
    };

    const handleOnBlur = (event: React.FocusEvent<HTMLInputElement>) => {
      toggleFocusInput();

      if (onBlur) {
        onBlur(event);
      }
    };

    return (
      <div className={clsx(styles.inputContainer, inputContainerClassName)}>
        {label && (
          <div className={clsx(styles.label, labelClassName)}>
            {label} {useRequiredSymbol && <div className={styles.requiredLabel}>*</div>}
          </div>
        )}
        <NumericFormat
          getInputRef={ref}
          className={clsx(styles.input, inputClassName, !!errorMessage && styles.inputError)}
          prefix={prefix}
          suffix={suffix}
          onChange={onChange}
          placeholder={placeholder}
          allowNegative={allowNegative}
          thousandSeparator={thousandSeparator}
          decimalScale={decimalPlacesAmount}
          onFocus={handleOnFocus}
          onBlur={handleOnBlur}
          name={name}
          maxLength={maxLength}
          max={max}
          disabled={disabled}
          value={value}
          isAllowed={isAllowed}
          pattern={pattern}
          {...rest}
        />
        {errorMessage && <div className={styles.errorMessage}>{errorMessage}</div>}
      </div>
    );
  },
);

export default NumberInput;
